/**
 * COMPANY-WORKSPACE-D2x — feature-local corporate DetailGallery video modal
 * ownership tracker.
 *
 * DetailGallery drives `#detailModalVideo` via Bootstrap data-bs-* attributes.
 * When React remounts/unmounts mid-show, Bootstrap can leave body scroll-lock
 * and a `.modal-backdrop`. This helper tracks ONLY the corporate video modal
 * session and cleans only residue that session owned.
 *
 * Runtime note (repository `frontend/public/js/bootstrap.bundle.min.js`,
 * Bootstrap 5.3.0-alpha3):
 * - `Modal.dispose()` does NOT cancel `executeAfterTransition` / `_queueCallback`
 *   timers. Queued `_showElement` / `_hideModal` may still run after dispose.
 * - `_hideModal()` schedules `backdrop.hide(completion)` whose completion
 *   directly calls `document.body.classList.remove("modal-open")` and
 *   `_scrollBar.reset()`. Replacing `instance._hideModal` AFTER that schedule
 *   cannot neutralize the already-queued backdrop completion.
 *
 * Teardown therefore:
 * 1. bumps an owner generation first;
 * 2. relies on a show-time patch of `_hideModal` that generation-gates the
 *    backdrop-hide completion before it can touch body scroll-lock;
 * 3. dispose()s, re-arms inert stubs for any still-queued show/hide methods;
 * 4. removes owned DOM residue / restores body from the pre-show snapshot.
 *
 * Non-goals: Bootstrap upgrade, global modal manager, shared overlay registry,
 * new dependencies.
 */

export const CORPORATE_VIDEO_MODAL_ID = "detailModalVideo";
export const CORPORATE_VIDEO_BACKDROP_ATTR =
  "data-company-property-video-backdrop";

const HIDE_MODAL_PATCHED = "__corporateVideoHideModalPatched";

function readBodyStyleSnapshot() {
  return {
    hadModalOpen: document.body.classList.contains("modal-open"),
    overflow: document.body.style.getPropertyValue("overflow"),
    overflowPriority: document.body.style.getPropertyPriority("overflow"),
    paddingRight: document.body.style.getPropertyValue("padding-right"),
    paddingRightPriority: document.body.style.getPropertyPriority(
      "padding-right",
    ),
  };
}

function restoreBodyFromSnapshot(preBody) {
  if (!preBody) return;

  // Only drop modal-open when THIS session introduced it.
  if (!preBody.hadModalOpen) {
    document.body.classList.remove("modal-open");
  }

  // Always restore the exact inline styles captured at show start so a
  // stacked session cannot leave Bootstrap's padding/overflow tweaks
  // behind while an unrelated overlay remains open.
  if (preBody.overflow) {
    document.body.style.setProperty(
      "overflow",
      preBody.overflow,
      preBody.overflowPriority || undefined,
    );
  } else {
    document.body.style.removeProperty("overflow");
  }

  if (preBody.paddingRight) {
    document.body.style.setProperty(
      "padding-right",
      preBody.paddingRight,
      preBody.paddingRightPriority || undefined,
    );
  } else {
    document.body.style.removeProperty("padding-right");
  }
}

/**
 * Patch Modal._hideModal so the backdrop-hide completion is generation-gated.
 * Bootstrap queues that completion inside _hideModal; once queued, stubbing
 * _hideModal cannot stop `document.body.classList.remove("modal-open")`.
 *
 * `getOwnerGeneration` must be a live getter over the owner's generation
 * counter so teardown invalidates completions scheduled earlier on the same
 * Modal instance.
 */
function patchHideModalForOwner(instance, getOwnerGeneration) {
  if (!instance || typeof instance !== "object") return;
  if (typeof getOwnerGeneration !== "function") return;
  if (instance[HIDE_MODAL_PATCHED]) return;
  instance[HIDE_MODAL_PATCHED] = true;

  instance._hideModal = function corporateVideoGatedHideModal() {
    const element = this._element;
    if (!element) return;

    element.style.display = "none";
    element.setAttribute("aria-hidden", "true");
    element.removeAttribute("aria-modal");
    element.removeAttribute("role");
    this._isTransitioning = false;

    const backdrop = this._backdrop;
    if (!backdrop || typeof backdrop.hide !== "function") return;

    // Capture generation at schedule time — teardown bumps generation so a
    // still-pending backdrop completion must not clear an unrelated lock.
    const scheduledGen = getOwnerGeneration();

    backdrop.hide(() => {
      if (scheduledGen !== getOwnerGeneration()) {
        return;
      }
      document.body.classList.remove("modal-open");
      if (typeof this._resetAdjustments === "function") {
        this._resetAdjustments();
      }
      if (this._scrollBar && typeof this._scrollBar.reset === "function") {
        this._scrollBar.reset();
      }
      element.dispatchEvent(new Event("hidden.bs.modal", { bubbles: true }));
    });
  };
}

/**
 * After BaseComponent.dispose() nulls own fields, queued transition callbacks
 * still close over the instance and invoke prototype `_showElement` /
 * `_hideModal`, which throw on null `_element`. Re-arm inert own-property
 * stubs so those surviving callbacks cannot resurrect UI or throw.
 */
function neutralizeDisposedModalCallbacks(instance) {
  if (!instance || typeof instance !== "object") return;
  instance._showElement = function corporateVideoNoopShowElement() {};
  instance._hideModal = function corporateVideoNoopHideModal() {};
  instance._resetAdjustments = function corporateVideoNoopResetAdjustments() {};
  instance._isTransitioning = false;
  instance._isShown = false;
  instance._config = { focus: false, backdrop: false, keyboard: false };
  instance._backdrop = {
    show(cb) {
      if (typeof cb === "function") cb();
    },
    hide(cb) {
      if (typeof cb === "function") cb();
    },
    dispose() {},
  };
  instance._focustrap = {
    activate() {},
    deactivate() {},
  };
  instance._scrollBar = {
    hide() {},
    reset() {},
  };
}

function getModalInstance(modalEl) {
  const ModalCtor =
    typeof window !== "undefined" ? window.bootstrap?.Modal : null;
  if (!modalEl || !ModalCtor || typeof ModalCtor.getInstance !== "function") {
    return null;
  }
  try {
    return ModalCtor.getInstance(modalEl);
  } catch (_err) {
    return null;
  }
}

function disposeBootstrapInstance(modalEl) {
  const instance = getModalInstance(modalEl);
  if (!instance) return;
  try {
    // dispose() releases the instance map entry and removes the Backdrop
    // element when still appended. It does NOT cancel pending
    // executeAfterTransition timers in repository Bootstrap 5.3.0-alpha3.
    instance.dispose();
  } catch (_err) {
    /* ignore — element may already be detached */
  }
  neutralizeDisposedModalCallbacks(instance);
}

function resetModalElementChrome(modalEl) {
  if (!modalEl || !modalEl.classList) return;
  modalEl.classList.remove("show");
  if (modalEl.style) {
    modalEl.style.display = "";
  }
  modalEl.setAttribute("aria-hidden", "true");
  modalEl.removeAttribute("aria-modal");
  modalEl.removeAttribute("role");
}

/**
 * @returns {{
 *   attach: (modalEl?: HTMLElement|null) => void,
 *   teardown: () => void,
 *   detach: () => void,
 * }}
 */
export function createCorporateVideoModalOwner(options = {}) {
  const modalId = options.modalId || CORPORATE_VIDEO_MODAL_ID;
  let modalEl = null;
  let listenersAttached = false;
  let session = null;
  let generation = 0;
  let captureTimer = null;

  const getOwnerGeneration = () => generation;

  const clearCaptureTimer = () => {
    if (captureTimer != null) {
      clearTimeout(captureTimer);
      captureTimer = null;
    }
  };

  const captureOwnedBackdrop = (gen, beforeSet) => {
    if (gen !== generation || !session || session.generation !== gen) return;
    const all = Array.from(document.querySelectorAll(".modal-backdrop"));
    const created = all.find((node) => !beforeSet.has(node));
    if (!created) return;
    created.setAttribute(CORPORATE_VIDEO_BACKDROP_ATTR, "1");
    session.ownedBackdrop = created;
  };

  const patchLiveModalInstance = () => {
    patchHideModalForOwner(getModalInstance(modalEl), getOwnerGeneration);
  };

  const handleShow = () => {
    generation += 1;
    const gen = generation;
    clearCaptureTimer();
    const beforeSet = new Set(document.querySelectorAll(".modal-backdrop"));
    session = {
      opened: true,
      generation: gen,
      preBody: readBodyStyleSnapshot(),
      ownedBackdrop: null,
    };
    // Patch before hide can schedule an ungated backdrop completion.
    patchLiveModalInstance();
    // Bootstrap may insert the backdrop sync or on the next task.
    captureOwnedBackdrop(gen, beforeSet);
    captureTimer = setTimeout(() => {
      captureTimer = null;
      captureOwnedBackdrop(gen, beforeSet);
    }, 0);
  };

  const handleHidden = () => {
    clearCaptureTimer();
    if (session?.ownedBackdrop?.getAttribute(CORPORATE_VIDEO_BACKDROP_ATTR)) {
      session.ownedBackdrop.removeAttribute(CORPORATE_VIDEO_BACKDROP_ATTR);
    }
    session = null;
  };

  const detachListeners = () => {
    if (modalEl && listenersAttached) {
      modalEl.removeEventListener("show.bs.modal", handleShow);
      modalEl.removeEventListener("hidden.bs.modal", handleHidden);
    }
    listenersAttached = false;
  };

  const attach = (nextEl) => {
    detachListeners();
    modalEl =
      nextEl ||
      (typeof document !== "undefined"
        ? document.getElementById(modalId)
        : null);
    if (!modalEl) return;
    modalEl.addEventListener("show.bs.modal", handleShow);
    modalEl.addEventListener("hidden.bs.modal", handleHidden);
    listenersAttached = true;
    // If a Bootstrap instance already exists (remount/return-to-ready), patch it.
    patchLiveModalInstance();
  };

  const teardown = () => {
    // Invalidate delayed backdrop capture AND any already-queued backdrop-hide
    // completion scheduled by a patched _hideModal.
    generation += 1;
    clearCaptureTimer();

    const activeSession = session;
    session = null;

    const el =
      modalEl ||
      (typeof document !== "undefined"
        ? document.getElementById(modalId)
        : null);

    disposeBootstrapInstance(el);
    resetModalElementChrome(el);

    const owned =
      activeSession?.ownedBackdrop ||
      (typeof document !== "undefined"
        ? document.querySelector(`[${CORPORATE_VIDEO_BACKDROP_ATTR}="1"]`)
        : null);
    if (owned && owned.parentNode) {
      owned.parentNode.removeChild(owned);
    }

    // Never opened → leave unrelated backdrops and body styles untouched.
    if (activeSession?.opened) {
      restoreBodyFromSnapshot(activeSession.preBody);
    }
  };

  const detach = () => {
    teardown();
    detachListeners();
    modalEl = null;
  };

  return {
    attach,
    teardown,
    detach,
  };
}

export default createCorporateVideoModalOwner;
